//React
import React from "react";
import PropTypes from "prop-types";
import InfoTip from "../inputs/infoTip";
//Assets
import xThickIcon from "../../assets/svgs/x-thick.svg";
import chevronDownIcon from "../../assets/svgs/chevron-down.svg";
//CSS
import "./builderCard.scss";

/**
 * The card's remove control: a grey disc with a white X (`md`, 18px icon, in the
 * header) or the smaller `sm` variant (14px icon) the V2 builder puts beside the
 * criteria-type picker of a card that has no header yet.
 */
export function BuilderCardRemoveButton({ onClick, label, size, className }) {
	return (
		<button
			type="button"
			className={[
				"as-builder-card__remove",
				size === "sm" ? "as-builder-card__remove--sm" : "",
				className ?? "",
			]
				.join(" ")
				.trim()}
			onClick={onClick}
			title={label ?? "Remove"}
			aria-label={label ?? "Remove"}
		>
			<img
				src={xThickIcon}
				alt=""
				aria-hidden="true"
			/>
		</button>
	);
}
BuilderCardRemoveButton.propTypes = {
	onClick: PropTypes.func,
	/** accessible name; default "Remove" */
	label: PropTypes.string,
	size: PropTypes.oneOf(["sm", "md"]),
	className: PropTypes.string,
};

/**
 * Builder card (ASB-4018): the framed block an audience page is built from —
 * green left border, a header with the title (+ optional count and InfoTip) and
 * the remove / collapse controls, and a body — exactly as the V2 Audience
 * Builder draws its criteria cards. Extracted from AudienceBuilderV2.js so the
 * signal and modeled pages can put their own blocks (a Slider, a read-only value,
 * a chart) in the same frame.
 *
 * Collapsing is the caller's: the card renders the chevron in the state you
 * give it (`collapsed`) and reports clicks (`onToggleCollapse`); what the body
 * shows when collapsed is up to the content (V2's criteria cards render their
 * own summary). Omit `title` for a headerless card (V2's empty criteria card,
 * which is just a type picker). `incomplete` turns the left border red.
 */
function BuilderCard({
	title,
	count,
	info,
	onRemove,
	removeLabel,
	collapsed,
	onToggleCollapse,
	incomplete,
	children,
	className,
}) {
	const hasHeader = title != null;
	const showRemove = typeof onRemove === "function";
	const showCollapse = typeof onToggleCollapse === "function";
	return (
		<div
			className={[
				"as-builder-card",
				incomplete ? "as-builder-card--incomplete" : "",
				className ?? "",
			]
				.join(" ")
				.trim()}
		>
			{hasHeader ? (
				<div className="as-builder-card__header">
					<span className="as-builder-card__title">
						{title}
						{count > 0 ? <span className="as-builder-card__count"> ({count})</span> : null}
						{info ? (
							<InfoTip
								content={info}
								label={typeof title === "string" ? `About ${title}` : undefined}
								className="as-builder-card__info"
							/>
						) : null}
					</span>
					<div className="as-builder-card__actions">
						{showRemove ? (
							<BuilderCardRemoveButton
								onClick={onRemove}
								label={removeLabel ?? "Remove"}
							/>
						) : null}
						{showCollapse ? (
							<>
								{showRemove ? <div className="as-builder-card__separator" /> : null}
								<button
									type="button"
									className={[
										"as-builder-card__collapse",
										collapsed ? "as-builder-card__collapse--collapsed" : "",
									]
										.join(" ")
										.trim()}
									onClick={onToggleCollapse}
									title={collapsed ? "Expand" : "Collapse"}
									aria-expanded={!collapsed}
								>
									<img
										src={chevronDownIcon}
										alt={collapsed ? "Expand" : "Collapse"}
									/>
								</button>
							</>
						) : null}
					</div>
				</div>
			) : null}
			<div className="as-builder-card__body">{children}</div>
		</div>
	);
}

BuilderCard.propTypes = {
	/** header title; omit for a headerless card */
	title: PropTypes.node,
	/** shown as " (n)" after the title when > 0 */
	count: PropTypes.number,
	/** InfoTip text after the title */
	info: PropTypes.string,
	/** present = the remove disc renders */
	onRemove: PropTypes.func,
	/** accessible name of the remove disc; default "Remove" */
	removeLabel: PropTypes.string,
	collapsed: PropTypes.bool,
	/** present = the collapse chevron renders */
	onToggleCollapse: PropTypes.func,
	/** validation state: red left border */
	incomplete: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string,
};

export default BuilderCard;
